import { useState, useRef } from 'react';
import type { Slot } from '../../types';
import WeaponRack from './WeaponRack';
import WeaponPlate from './WeaponPlate';
import ArmourySlotMenu from './ArmourySlotMenu';
import WeaponIcon from '../WeaponIcon';
import LoadoutMenu from '../LoadoutMenu';
import RackToolbar from './RackToolbar';
import styles from '../../styles/WeaponRack.module.css';
import { useAttacker } from '../../contexts/AttackerContext';
import { useRosterContext } from '../../contexts/RosterContext';
import { useSession } from '../../contexts/SessionContext';
import { filterRack, isFiltering } from '../../engine/roster/rack';
import type { RackFilter } from '../../engine/roster/rack';
import { circularOffset, plateKeyIntent } from './drum';
import { useDrumCursor, useDrumLayout, useDrumWheel, useFocusFollowsCursor } from './useDrum';

/**
 * The Armoury tab.
 *
 * The only file under armoury/ that touches contexts - WeaponRack is
 * layout and WeaponPlate is one weapon, both fed entirely by props. It
 * also owns the two pieces of local interaction state (what is being
 * dragged, where the context menu is) and renders the menu itself, since
 * a plate is a `container-type` element and cannot host a fixed child.
 *
 * The drum's behaviour (cursor, swing, rotation, wheel) is `useDrum`,
 * its arithmetic `drum.ts`.
 */

/** Recession is clamped at four plates out, past which it stops meaning anything. */
const RECEDE_MAX = 4;

/** What the Armoury reads off the attacker bucket (which arrives untyped). */
interface ArmoryAttacker {
  currentSlots: Slot[];
  selectedSlot: number | null;
  setSelectedSlot: (index: number) => void;
  setEditingSlot: (index: number) => void;
  slotClipboard: { weaponName: string; sourceProfile: string } | null;
  setSlotClipboard: (clip: null) => void;
  localCurrentProfile: string;
  handleDuplicateSlot: (index: number) => void;
  handleCopySlot: (index: number) => void;
  handleCutSlot: (index: number) => void;
  handlePasteSlot: (index: number) => void;
  handleDeleteSlot: (index: number) => void;
  handleMoveSlot: (from: number, to: number) => void;
  handleMoveSlotBy: (index: number, delta: number) => void;
  actorTarget?: { parryWeaponName?: string } | null;
}

type Menu = { index: number; x: number; y: number; onPlate: boolean };

/**
 * The rack's filter and the drum over what it shows.
 *
 * Only a text SEARCH parks the drum flat - results read as a list. A
 * type chip narrows the cylinder instead: the drum keeps turning over
 * the visible subset (v8.199.10). Reorder stays disabled under ANY
 * filter (`filtering`) - the order is load-bearing either way.
 */
function useArmoryDrum(currentSlots: Slot[], selectedSlot: number | null) {
  const [filter, setFilter] = useState<RackFilter>({});
  const searching = !!filter.query?.trim();
  /**
   * What the rack is showing, each plate carrying its REAL position.
   * Every handler downstream - arm, move, delete, the 1-9 keys - speaks
   * rack positions, so the filtered view must never renumber.
   */
  const visible = filterRack(currentSlots, filter);
  /** The cylinder's chambers: the visible plates' REAL rack positions, in order. */
  const ring = visible.map((h) => h.index);
  const count = currentSlots.length;
  const { cursor, navigate } = useDrumCursor({ selectedSlot, count, ring, searching });
  const plates = useRef<(HTMLButtonElement | null)[]>([]);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  useDrumLayout({ viewportRef, plates, ring, cursor, searching, count, selectedSlot });
  const onWheel = useDrumWheel(ring, searching, navigate);
  useFocusFollowsCursor(plates, cursor, count);
  const offsetOf = (i: number) => circularOffset(ring, cursor, i);
  const filtering = isFiltering(filter);
  return {
    filter,
    setFilter,
    filtering,
    searching,
    visible,
    ring,
    cursor,
    navigate,
    plates,
    viewportRef,
    onWheel,
    offsetOf,
  };
}

type Drum = ReturnType<typeof useArmoryDrum>;

/** "＋ Arm a weapon", or Paste while the clipboard holds one. */
function RackAddButton({ attacker, locked, onAdd }: { attacker: ArmoryAttacker; locked: boolean; onAdd: () => void }) {
  const clip = attacker.slotClipboard;
  return (
    <button
      type="button"
      className={styles.addBtn}
      data-testid="rack-add"
      aria-label={clip ? `Paste ${clip.weaponName}` : 'Arm a weapon'}
      disabled={locked}
      onClick={(e) => {
        e.stopPropagation();
        onAdd();
      }}
    >
      <WeaponIcon name="arm" size={13} />
      {clip ? `Paste ${clip.weaponName}` : 'Arm a weapon'}
    </button>
  );
}

/** The clipboard bar: what is held, from whose profile, and a way to drop it. */
function ClipboardBar({ attacker }: { attacker: ArmoryAttacker }) {
  const clip = attacker.slotClipboard!;
  return (
    <div className={styles.clipboard}>
      <span>
        Holding <b>{clip.weaponName}</b>
        {clip.sourceProfile !== attacker.localCurrentProfile ? ` from ${clip.sourceProfile}` : ''}
      </span>
      <button
        type="button"
        className={styles.clipboardClear}
        aria-label="Drop the clipboard weapon"
        onClick={() => attacker.setSlotClipboard(null)}
      >
        ✕
      </button>
    </div>
  );
}

interface PlatesProps {
  attacker: ArmoryAttacker;
  drum: Drum;
  locked: boolean;
  onKeyDown: (e: React.KeyboardEvent, index: number) => void;
  onMenu: (menu: Menu) => void;
  onOpenControls: (() => void) | null;
}

/** The visible plates, each placed on the drum; owns the drag state. */
function ArmoryPlates({ attacker, drum, locked, onKeyDown, onMenu, onOpenControls }: PlatesProps) {
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);
  /**
   * Which weapon actually parries, asked of the DEFENDER PROJECTION rather
   * than inferred from rack position.
   *
   * Rack order does feed it - `slotsToWeapons` rebuilds `sheet.weapons` in
   * rack order and `findParryWeaponIndex` takes the first non-Unwieldy
   * melee weapon - but the Gear tab can reorder `defenderWeapons` into
   * `overrides`, and that then outranks the rack for good. Reading
   * position here would make the marker lie the moment anyone used it.
   */
  const parryWeaponName = attacker.actorTarget?.parryWeaponName;
  const endDrag = () => {
    setDragFrom(null);
    setDropAt(null);
  };
  // Markup: one WeaponPlate's props, one per line - long by nature, not by logic.
  return drum.visible.map(({ slot, index: i }: { slot: Slot; index: number }) => (
    <WeaponPlate
      key={slot.weaponId ?? i}
      slot={slot}
      index={i}
      total={attacker.currentSlots.length}
      armed={attacker.selectedSlot === i}
      locked={locked || drum.filtering}
      dragging={dragFrom === i}
      dropBefore={dropAt === i && dragFrom !== null && dragFrom !== i}
      distance={Math.min(Math.abs(drum.offsetOf(i)), RECEDE_MAX)}
      direction={(Math.sign(drum.offsetOf(i)) as -1 | 0 | 1) ?? 0}
      isParryWeapon={!!parryWeaponName && slot.weaponName === parryWeaponName}
      focused={drum.cursor === i}
      plateRef={(el) => {
        drum.plates.current[i] = el;
      }}
      onKeyDown={onKeyDown}
      onSelect={attacker.setSelectedSlot}
      onEdit={attacker.setEditingSlot}
      onConfigure={(idx: number) => {
        attacker.setSelectedSlot(idx);
        onOpenControls?.();
      }}
      onContextMenu={(index, x, y) => onMenu({ index, x, y, onPlate: true })}
      onMoveBy={drum.filtering ? undefined : attacker.handleMoveSlotBy}
      onDragStart={setDragFrom}
      onDragOver={setDropAt}
      onDragEnd={endDrag}
      onDrop={(to) => {
        if (dragFrom !== null) attacker.handleMoveSlot(dragFrom, to);
        endDrag();
      }}
    />
  ));
}

/**
 * Ctrl+Arrow moves the WEAPON, never past either end, and says so for a
 * screen reader: a reorder is otherwise silent - focus stays on the same
 * weapon and its label does not change. Motion is never the only channel.
 */
function useReorder(attacker: ArmoryAttacker, drum: Drum, announce: (text: string) => void) {
  const { currentSlots } = attacker;
  return (index: number, delta: 1 | -1) => {
    const to = index + delta;
    if (to < 0 || to > currentSlots.length - 1) return;
    attacker.handleMoveSlotBy(index, delta);
    drum.navigate(to);
    announce(`${currentSlots[index].weaponName} moved to position ${to + 1} of ${currentSlots.length}`);
  };
}

/** The plate keyboard: arrows move the cursor round the drum, Ctrl+Arrow reorders (`plateKeyIntent`). */
function usePlateKeys(attacker: ArmoryAttacker, drum: Drum, announce: (text: string) => void) {
  const reorder = useReorder(attacker, drum, announce);
  return (e: React.KeyboardEvent, index: number) => {
    const intent = plateKeyIntent(e, index, drum.ring, attacker.currentSlots.length - 1);
    if (!intent) return;
    e.preventDefault();
    if (intent.kind === 'goto') drum.navigate(intent.to);
    else reorder(index, intent.delta);
  };
}

/** The toolbar's loadout menu, over the acting combatant's kits. */
function ArmoryLoadouts({ locked }: { locked: boolean }) {
  const {
    actor,
    loadouts,
    kitPlace,
    libraryKits,
    onSaveLoadoutToLibrary,
    onSaveLoadout,
    onApplyLoadout,
    onDeleteLoadout,
  } = useRosterContext();
  return (
    <LoadoutMenu
      actor={actor}
      loadouts={loadouts || []}
      place={kitPlace ?? 'browser'}
      libraryKits={libraryKits}
      onSaveToLibrary={onSaveLoadoutToLibrary}
      onSave={onSaveLoadout}
      onApply={onApplyLoadout}
      onDelete={onDeleteLoadout}
      disabled={locked}
    />
  );
}

export default function ArmoryTab({ onOpenControls = null }: { onOpenControls?: (() => void) | null } = {}) {
  const attacker: ArmoryAttacker = useAttacker();
  const { actor } = useRosterContext();
  const { isLocked } = useSession();
  const locked = isLocked('editProfiles');
  const drum = useArmoryDrum(attacker.currentSlots, attacker.selectedSlot);
  const [menu, setMenu] = useState<Menu | null>(null);
  /** What just happened, for a screen reader (see `usePlateKeys`). */
  const [announcement, setAnnouncement] = useState('');
  const onPlateKeyDown = usePlateKeys(attacker, drum, setAnnouncement);
  const closeMenu = () => setMenu(null);
  const { currentSlots, slotClipboard } = attacker;
  const addWeapon = () => {
    closeMenu();
    // A position past the end IS the append: SlotEditor falls back to
    // emptySlot(editingSlot) when the rack has nothing there.
    if (slotClipboard) attacker.handlePasteSlot(currentSlots.length);
    else attacker.setEditingSlot(currentSlots.length);
  };
  return (
    <>
      <WeaponRack
        isEmpty={currentSlots.length === 0}
        onBackgroundClick={closeMenu}
        viewportRef={drum.viewportRef}
        flat={drum.searching}
        onWheel={drum.onWheel}
        toolbar={
          <RackToolbar
            filter={drum.filter}
            onFilterChange={drum.setFilter}
            shown={drum.visible.length}
            total={currentSlots.length}
            trailing={<ArmoryLoadouts locked={locked} />}
          />
        }
        emptyHint={actor ? `${actor.name} is carrying nothing.` : 'Nothing racked.'}
        addSlot={<RackAddButton attacker={attacker} locked={locked} onAdd={addWeapon} />}
        footer={slotClipboard && <ClipboardBar attacker={attacker} />}
      >
        <ArmoryPlates {...{ attacker, drum, locked, onOpenControls }} onKeyDown={onPlateKeyDown} onMenu={setMenu} />
      </WeaponRack>

      <span className={styles.srOnly} role="status" aria-live="polite">
        {announcement}
      </span>

      {menu && <ArmoryMenu attacker={attacker} menu={menu} onClose={closeMenu} />}
    </>
  );
}

/** The plate's context menu, portalled out of the plate by `ArmourySlotMenu`. */
function ArmoryMenu({ attacker, menu, onClose }: { attacker: ArmoryAttacker; menu: Menu; onClose: () => void }) {
  return (
    <ArmourySlotMenu
      {...menu}
      clipboardName={attacker.slotClipboard?.weaponName}
      onEdit={(i: number) => {
        onClose();
        attacker.setEditingSlot(i);
      }}
      onDuplicate={attacker.handleDuplicateSlot}
      onCopy={attacker.handleCopySlot}
      onCut={attacker.handleCutSlot}
      onPaste={attacker.handlePasteSlot}
      onDelete={attacker.handleDeleteSlot}
      onClose={onClose}
    />
  );
}
